// src/contexts/AuthContext.js
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Define checkUserSession first so it can be used in effects
  const checkUserSession = useCallback(async (isBackground = false) => {
    // Prevent multiple simultaneous checks if already loading initially
    // But allow re-checks if called manually (refreshAuth)

    try {
      if (!API_URL) {
        throw new Error('REACT_APP_API_URL is not configured');
      }
      // Don't set loading to true if we implicitly know it's a background refresh
      // or if we are just checking status without blocking UI
      if (!isBackground) {
        setLoading(true);
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 25000);

      const res = await fetch(`${API_URL}/api/profile`, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const userData = await res.json();
          setUser(userData);
          setError(null);
        } else {
          console.error('[AuthContext] Expected JSON but got:', contentType);
          throw new Error('Invalid response from auth endpoint');
        }
      } else if (res.status === 401) {
        setUser(null);
        setError(null);
      } else {
        console.error('[AuthContext] Auth check failed:', res.status);
        setError(`Authentication failed: ${res.status}`);
      }



    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[AuthContext] Error checking session:', err.message);
        setError(err.message || String(err));
      }
      // Only clear user on error if we don't have one (prevent clearing on blip?)
      // usage in useAuth was: setUser(null) on error.
      setUser(null);
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
    }
  }, [API_URL]);

  // Initial auth check on mount
  useEffect(() => {
    checkUserSession();
  }, [checkUserSession]);

  // Periodic refresh
  useEffect(() => {
    if (!user) return;
    const refreshInterval = setInterval(() => {
      checkUserSession(true);
    }, 5 * 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, [user, checkUserSession]);

  const refreshAuth = useCallback(() => {
    return checkUserSession();
  }, [checkUserSession]);

  const logout = useCallback(async () => {
    // Optimistically clear user immediately
    setUser(null);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/logout`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        console.error('[AuthContext] Logout request failed:', response.status);
      }
    } catch (err) {
      console.error('[AuthContext] Error during logout:', err);
    }
  }, [API_URL]);

  const value = {
    user,
    setUser,
    loading,
    error,
    API_URL,
    refreshAuth,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within an AuthProvider');
  return ctx;
}
